import { InlineValue, Link, useContents, usePageContent, WithHandlers } from "@appiodev/xcore-client/xcore-ui";
import { Box, Button, Container, Flex, Stack, Text, Typography } from "@xcorejs/ui";
import PdfIcon from "components/icons/16/pdf.svg";
import ImagesCarousel from "components/ImagesCarousel";
import Layout from "components/Layout";
import MoreNews from "components/news/MoreNews";
import NewsStack from "components/news/NewsStack";
import RobeHead from "components/RobeHead";
import ProductCard from "design-system/robe/cards/Product";
import LeftMenu from "design-system/robe/LeftMenuContainer/LeftMenu";
import Popover from "design-system/robe/Popover";
import { FC, useEffect, useState } from "react";
import { useTagManager } from "utils/useTagManager";
import { getRobePaths, getRobeProps, RobePage, useLayout } from "xcore";
import { News, NewsDetailPage } from "xcore/types";

import { useRouter } from "next/router";
import { RssButton } from "../../components/RssButton";
import RobeRichtext from "components/RobeRichtext";
import { useNewsGoogleStructuredData } from "utils/googleStructuredData/useNewsGoogleStructuredData";
import styled from "styled-components";

export const getStaticProps = getRobeProps(async ({ cms, id }) => {
  const pageContent = await cms.content.single<News>(id!, {
    preview: true,
    fetchRichtextLinks: true,
    relations: v => [
      v.products?.$options(["images", "discontinuedGlob", "discontinuedReg", "name"])
    ]
  });
  return {
    pageContent,
    contents: [
      cms.content.list<News>("news", {
        fields: ["title", "intro", "images", "size", "thumbnail"],
        categories: [pageContent?.content.categories?.["news-categories"]?.map(c => c.id) ?? []],
        includeFiles: true,
        includeCategories: true,
        sort: ["createdAt", "desc"],
        perPage: 5
      }),
      cms.content.list<NewsDetailPage>("newsDetailPage", { fields: ["productInvolved", "showPdfBtn", "discoverMoreNews"] })
    ]
  };
});

export const getStaticPaths = getRobePaths("news");

const NewsPage: RobePage = () => {
  const { locale } = useRouter();
  const [news, { stringify, value, file }] = usePageContent<News>();
  const [, [newsDetailPage]] = useContents<[News, NewsDetailPage]>();
  const { values } = news;

  useTagManager("news-detail");

  const newsGoogleStructuredData = useNewsGoogleStructuredData();

  return (
    <>
      <RobeHead
        googleStructuredData={newsGoogleStructuredData}
        title={[stringify(values.title)]}
        description={stringify(values.title)}
        img={values.thumbnail
          ? file(values.thumbnail, { width: 630, height: 450 })
          : file(values.images?.[0], { width: 1290, height: 726 })}
      />

      <Container
        flexDirection="column"
        paddingTop={{ _: "3rem", sm: "5rem" }}
        paddingBottom={{ _: "6rem", sm: "10rem" }}
      >
        <NewsArticle news={news} newsDetailPage={newsDetailPage} />

        {values.images && (
          <Box mt={{ _: "4rem", sm: "8rem" }} mb={{ _: "2rem", sm: "8rem" }}>
            <ImagesCarousel
              fitToPageContent
              disableScroll
              hideSubHeader
              images={value(values.images)!.map(img => [
                file(img, value(values.images)!.length > 1 ? { width: 740, height: 416, fit: "inside" } : { width: 1290, height: 726 }),
                value(img.name),
                file(img, value(values.images)!.length > 1
                  ? { width: 740, height: 416, enlargement: 2, fit: "inside" }
                  : { width: 1290, height: 726, enlargement: 2 }),
                img.id
              ])}
            />
          </Box>
        )}
        <Flex justifyContent="center" flexWrap="wrap" width="100%">
          <Button
            as="a"
            mb="8rem"
            target="_blank"
            href={newsPdfUrl(news.id, locale ?? "en")}
            bg="black"
            leftIcon={<PdfIcon />}
            boxShadow="0 3.5rem 2.5rem -2.5rem rgba(30, 30, 30, 1)"
          >
            {stringify(newsDetailPage.values.showPdfBtn)}
          </Button>
        </Flex>

        <MoreNews />
      </Container>
    </>
  );
};

NewsPage.Layout = Layout;

export default NewsPage;

export const NewsArticle: FC<{ news: WithHandlers<News>; newsDetailPage: NewsDetailPage; pdf?: boolean }> =
({ news: { id, publishedAt, values }, newsDetailPage, pdf }) => {
  const { stringify, formatDate, value } = useLayout();
  const [isRssCopied, setIsRssCopied] = useState<boolean>(false);

  const buzzsproutAccountId = process.env.BUZZSPROUT_ACCOUNT_ID ?? "2411650";

  // Script has to run like this because React does not support automatic script executions in the JSX
  useEffect(() => {
    const buzzsproutEpisodeId = value(values.buzzsproutEpisodeId);
    if (buzzsproutEpisodeId) {
      const script = document.createElement("script");
      script.src = `https://www.buzzsprout.com/${buzzsproutAccountId}/episodes/${buzzsproutEpisodeId}.js?container_id=buzzsprout-player-${buzzsproutEpisodeId}&player=small`;
      script.type = "text/javascript";
      script.charset = "utf-8";
      script.async = true;

      document.body.appendChild(script);

      // Remove the script when the component unmounts
      return () => {
        document.body.removeChild(script);
      };
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [buzzsproutAccountId, values.buzzsproutEpisodeId]);

  return (
    <>
      <Box width="85rem" maxWidth="100%" margin="auto">
        <Box display="flex" height="4rem" alignItems="center">
          {stringify(publishedAt) && (
            <Text
              width="100%"
              as="time"
              fontSize="1.2rem"
              lineHeight="1.6rem"
              color="#939598"
              textAlign="center"
              dangerouslySetInnerHTML={{
              // eslint-disable-next-line @typescript-eslint/naming-convention
                __html: formatDate(
                  stringify(publishedAt)
                ).replace(/\//g, "&nbsp;/&nbsp;")
              }}
            />
          )}

          {!pdf && (
            <Box position="relative" display="flex" alignItems="center">
              <RssButton position="absolute" right={0} isRssCopied={isRssCopied} setIsRssCopied={setIsRssCopied} />
            </Box>
          )}
        </Box>
        <Typography variant="h1" textAlign="center" mt={{ _: "3rem", sm: "0.5rem" }}>
          <InlineValue value={value(values.title)} />
        </Typography>

        {values.products && (
          <Box width="100%" mt="3rem">
            <Typography variant="h3" as="h2" mb={{ _: ".5rem", md: "1.5rem" }} textAlign={{ _: "center" }}>
              {stringify(newsDetailPage.values.productInvolved)}
            </Typography>
            <LeftMenu>
              <Stack
                gap="1.5rem"
                direction="row"
                justifyContent="center"
                flexWrap="wrap"
                {...{ flexFlow: "wrap" } as any}
              >
                {values.products.map(p => (
                  <Popover
                    key={value(p.id)}
                    popover={(
                      <Link content={value(p)} noUILink>
                        <ProductCard product={value(p)} width="30rem" cursor="pointer" />
                      </Link>
                    )}
                    _popover={{
                      marginLeft: { _: "0", md: "50%" },
                      left: { _: "50%", md: "auto" },
                      borderTop: "0.6rem solid transparent"
                    }}
                  >
                    <Link content={value(p)} query={`?backto=${value(id)}`} mt=".5rem">
                      <span>{stringify(p.title)}</span>
                    </Link>
                  </Popover>
                ))}
              </Stack>
            </LeftMenu>
          </Box>
        )}

        {value(values.buzzsproutEpisodeId) && (
          <PlayerWrapper id={`buzzsprout-player-${value(values.buzzsproutEpisodeId)!}`} width={{ _: "100%", sm: "80%" }} margin="0 auto" my="3rem"></PlayerWrapper>
        )}

        {stringify(values.intro) && (
          <Typography variant="lead" textAlign="center" mt={value(values.buzzsproutEpisodeId) ? 0 : "4rem"} mb="3rem">
            {stringify(values.intro)}
          </Typography>
        )}

        {stringify(values.html) && (
          <Box mb={{ _: "3rem" }}>
            <div
              dangerouslySetInnerHTML={{
                // eslint-disable-next-line @typescript-eslint/naming-convention
                __html: stringify(values.html)!
              }}
            >
            </div>
          </Box>
        )}
      </Box>

      <Box width="85rem" maxWidth="100%" margin="auto">
        <NewsStack>
          <RobeRichtext value={values.content} _strong={{ color: "darkGray" }} />
        </NewsStack>
      </Box>
    </>
  );
};

export const newsPdfUrl = (id: number, locale: string) =>
  `/api/robe/pdfs/${locale}/news/${id}`;

const PlayerWrapper = styled(Box)`
  /* target the injected iframe and zoom it out slightly, otherwise right side of the elements gets cut off */
  & iframe {
    transform: scale(0.95);
  }
`;
